/** Pieces several remote views share: a composer, a decision card, an approval card, agent rows. */

import { time } from '@aioe/shared/wording/dateTime';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { Button, inputClass, StatusMark, Tick } from '../../components/ui.tsx';
import { timeAgo } from '../../hooks.ts';
import type { ActivityAgent, Decision, Permission, RingEvent, Workbench } from '../useWorkbench.ts';

export function agentState(status: string): { state: 'working' | 'idle' | 'error' | 'medium'; label: string } {
  if (status === 'thinking' || status === 'tool_calling' || status === 'starting') return { state: 'working', label: status.replace('_', ' ') };
  if (status === 'waiting_approval') return { state: 'medium', label: 'waiting for approval' };
  if (status === 'error') return { state: 'error', label: 'error' };
  return { state: 'idle', label: status };
}

/** One agent as a row: name, state in words, what it is doing. Selection is the row itself; no per-row buttons. */
export function AgentRow({ a, selected, onSelect }: { a: ActivityAgent; selected: boolean; onSelect: () => void }) {
  const s = agentState(a.status);
  return (
    <button type="button" onClick={onSelect} className={`w-full rounded-lg px-2 py-1.5 text-left hover:bg-surface-secondary ${selected ? 'bg-surface-secondary' : ''}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="truncate font-medium">{a.name}</span>
        {a.isOpsManager && <span className="text-2xs text-muted">operations manager</span>}
        {a.permissions.length > 0 && <span className="ml-auto shrink-0 text-2xs text-warning">{a.permissions.length} to approve</span>}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted">
        <StatusMark state={s.state} label={s.label} />
        <span className="truncate">{[a.currentTool ? `tool: ${a.currentTool}` : a.title, a.model ?? a.provider, a.tasks[0] ? `${a.tasks[0].id} ${a.tasks[0].title}` : undefined].filter(Boolean).join(' · ')}</span>
      </div>
    </button>
  );
}

export function ApprovalCard({ w, p, onDone }: { w: Workbench; p: Permission; onDone: () => void }) {
  const resolve = (decision: 'allow' | 'deny', optionId?: string) => void w.act(decision === 'allow' ? 'Allow' : 'Deny', () => w.call('permissions.resolve', { sessionId: p.sessionId, permissionId: p.permissionId, decision, optionId }), onDone);
  const always = p.options?.find((o) => o.kind === 'allow_always');
  return (
    <div>
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium">{p.agentName ?? 'Agent'}</span>
        {p.teamName && <span className="text-muted">{p.teamName}</span>}
        <StatusMark state={p.risk} label={`${p.risk} risk`} />
      </div>
      <div className="mt-0.5 text-sm">{p.title ?? p.kind ?? 'Tool call'}</div>
      <div className="mt-1.5 flex gap-2">
        <Button size="sm" onPress={() => resolve('allow')}>
          Allow
        </Button>
        {always && (
          <Button size="sm" variant="secondary" onPress={() => resolve('allow', always.id)}>
            Always
          </Button>
        )}
        <Button size="sm" variant="danger-soft" onPress={() => resolve('deny')}>
          Deny
        </Button>
      </div>
    </div>
  );
}

export function DecisionCard({ w, d, onDone }: { w: Workbench; d: Decision; onDone: () => void }) {
  const [text, setText] = useState('');
  const [multi, setMulti] = useState<string[]>(Array.isArray(d.default) ? d.default : []);
  const answer = (value: string | string[]) => void w.act('Answer', () => w.call('decisions.answer', { teamId: d.teamId, id: d.id, value }), onDone);
  return (
    <div>
      <div className="text-xs text-muted">
        {d.askedBy} asked {timeAgo(d.askedAt)}
        {d.taskId ? ` · ${d.taskId}` : ''} · {d.blocking === 'block' ? 'waiting on you' : 'proceeding on the default'}
      </div>
      <div className="mt-0.5 text-sm font-medium">{d.question}</div>
      {d.why && <div className="mt-0.5 text-xs text-muted">{d.why}</div>}
      <div className="mt-2">
        {d.kind === 'text' ? (
          <div className="flex gap-2">
            <input className={inputClass} value={text} onChange={(e) => setText(e.target.value)} placeholder="Your answer" onKeyDown={(e) => e.key === 'Enter' && text.trim() && answer(text.trim())} />
            <Button size="sm" isDisabled={!text.trim()} onPress={() => answer(text.trim())}>
              Answer
            </Button>
          </div>
        ) : d.kind === 'multi' ? (
          <div className="flex flex-col gap-1">
            {d.options.map((o) => (
              <Tick key={o.id} checked={multi.includes(o.id)} onChange={(v) => setMulti(v ? [...multi, o.id] : multi.filter((x) => x !== o.id))}>
                <span className="text-sm">{o.label}</span>
                {o.detail && <span className="ml-2 text-xs text-muted">{o.detail}</span>}
              </Tick>
            ))}
            <div>
              <Button size="sm" isDisabled={!multi.length} onPress={() => answer(multi)}>
                Answer
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {d.options.map((o, i) => (
              <Button key={o.id} size="sm" variant={i === 0 || o.id === d.default ? 'primary' : 'secondary'} onPress={() => answer(o.id)}>
                {o.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export type ComposeMode = 'prompt' | 'steer';

/** The composer: a prompt to an agent, or a steer mid-turn. Ctrl+Enter sends. */
export function Composer({ w, sessionId, agentName, placeholder }: { w: Workbench; sessionId?: string; agentName?: string; placeholder?: string }) {
  const [mode, setMode] = useState<ComposeMode>('prompt');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  async function send() {
    if (!text.trim() || !sessionId) return;
    setBusy(true);
    const ok = await w.act(mode === 'steer' ? 'Steer' : 'Send', () => w.call(mode === 'steer' ? 'sessions.steer' : 'sessions.prompt', { sessionId, text: text.trim(), source: 'aioe-console' }));
    if (ok) setText('');
    setBusy(false);
  }
  return (
    <div className="border-t border-separator pt-3">
      <div className="flex items-center gap-3">
        <Segment aria-label="How to send" size="sm" selectedKey={mode} onSelectionChange={(k) => setMode(String(k) as ComposeMode)}>
          <Segment.Item id="prompt">Prompt</Segment.Item>
          <Segment.Item id="steer">Steer mid-turn</Segment.Item>
        </Segment>
        {agentName && <span className="truncate text-xs text-muted">to {agentName}</span>}
      </div>
      <div className="mt-2 flex gap-2">
        <textarea
          className={`${inputClass} min-h-16`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={sessionId ? (placeholder ?? `Message ${agentName ?? 'the agent'}`) : 'Pick an agent first'}
          disabled={!w.connected || !sessionId}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
          }}
        />
        <Button size="sm" isPending={busy} isDisabled={!text.trim() || !w.connected || !sessionId} onPress={() => void send()}>
          Send
        </Button>
      </div>
    </div>
  );
}

/** Events for one session (or all), newest first, rendered as a timeline. */
export function EventList({ events, sessionId, limit = 120, empty }: { events: RingEvent[]; sessionId?: string; limit?: number; empty: string }) {
  const rows = events.filter((e) => !sessionId || (e.payload as { sessionId?: string }).sessionId === sessionId).slice(0, limit);
  if (!rows.length) return <p className="text-sm text-muted">{empty}</p>;
  return (
    <ul className="space-y-1 text-xs">
      {rows.map((e) => {
        const p = e.payload as Record<string, unknown>;
        const text = (p.title ?? p.message ?? p.status ?? p.stopReason ?? p.name ?? p.action ?? '') as string;
        return (
          <li key={e.seq} className="flex gap-2">
            <span className="mono w-16 shrink-0 text-muted">{time(e.at, { seconds: true })}</span>
            <span className="w-36 shrink-0 truncate font-medium">{e.kind}</span>
            <span className="min-w-0 flex-1 truncate text-muted" title={JSON.stringify(p)}>
              {text ? String(text) : JSON.stringify(p)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
